import { type Static, Type } from '@sinclair/typebox';
import { PaginateDTOSchema } from '../dto/common.dto.js';
import { PodcastDTOSchema } from '../dto/podcast.dto.js';
import {
  StripeApiListDTOSchema,
  StripeCustomerDTOSchema,
  StripeInvoiceDTOSchema,
  StripeInvoiceLineItemDTOSchema,
  StripeLineItemDTOSchema,
  StripePaymentIntentDTOSchema,
  StripeSubscriptionDTOSchema,
} from '../dto/stripe.dto.js';
import { type StripeTransferDTO, StripeTransferDTOSchema } from '../dto/stripe-transfer.dto.js';
import type { PaginateResult } from '../models/paginate-result.js';

export const StripeDetachPaymentMethodBodySchema = Type.Object({
  paymentMethodId: Type.String(),
});
export type StripeDetachPaymentMethodBody = Static<typeof StripeDetachPaymentMethodBodySchema>;

export const StripeDetachPaymentMethodReplySchema = StripeCustomerDTOSchema;
export type StripeDetachPaymentMethodReply = Static<typeof StripeDetachPaymentMethodReplySchema>;

// ---------------------------------------------------------------------------------------------------------------------

export const StripeCreateCheckoutSessionBodySchema = Type.Object({
  channelId: Type.Optional(Type.String()),
  fromUrl: Type.String(),
  mode: Type.Union([Type.Literal('subscription'), Type.Literal('setup')], { default: 'subscription' }),
  tierId: Type.Optional(Type.String()),
});
export type StripeCreateCheckoutSessionBody = Static<typeof StripeCreateCheckoutSessionBodySchema>;

export const StripeCreateCheckoutSessionReplySchema = Type.Object({ url: Type.String() });
export type StripeCreateCheckoutSessionReply = Static<typeof StripeCreateCheckoutSessionReplySchema>;

// ---------------------------------------------------------------------------------------------------------------------

export const StripeDowngradeSubscriptionBodySchema = Type.Object({
  subscriptionId: Type.String(),
  priceId: Type.String(),
});
export type StripeDowngradeSubscriptionBody = Static<typeof StripeDowngradeSubscriptionBodySchema>;

export const StripeDowngradeSubscriptionReplySchema = StripeCustomerDTOSchema;
export type StripeDowngradeSubscriptionReply = Static<typeof StripeDowngradeSubscriptionReplySchema>;

// ---------------------------------------------------------------------------------------------------------------------

export const StripeOnboardingQuerystringSchema = Type.Object({
  returnUrl: Type.String(),
});
export type StripeOnboardingQuerystring = Static<typeof StripeOnboardingQuerystringSchema>;

export const StripeOnboardingReplySchema = Type.Object({
  created: Type.Number(),
  expires_at: Type.Number(),
  object: Type.Literal('account_link'),
  url: Type.String(),
});
export type StripeOnboardingReply = Static<typeof StripeOnboardingReplySchema>;

// ---------------------------------------------------------------------------------------------------------------------

export const StripeListTransfersParamsSchema = Type.Object({ stripeAccountId: Type.String() });
export type StripeListTransfersParams = Static<typeof StripeListTransfersParamsSchema>;

export const StripeListTransfersQuerystringSchema = Type.Object({ page: Type.Number() });
export type StripeListTransfersQuerystring = Static<typeof StripeListTransfersQuerystringSchema>;

export const StripeListTransfersReplySchema = PaginateDTOSchema(StripeTransferDTOSchema);
export type StripeListTransfersReply = PaginateResult<StripeTransferDTO>;

// ---------------------------------------------------------------------------------------------------------------------

export const StripeRetrieveSessionQuerystringSchema = Type.Object({
  session_id: Type.Optional(Type.String()),
  payment_intent: Type.Optional(Type.String()),
});
export type StripeRetrieveSessionQuerystring = Static<typeof StripeRetrieveSessionQuerystringSchema>;

export const StripeRetrieveSessionReplySchema = Type.Object({
  channel: PodcastDTOSchema,
  invoice: StripeInvoiceDTOSchema,
  lineItem: Type.Union([StripeLineItemDTOSchema, StripeInvoiceLineItemDTOSchema]),
});
export type StripeRetrieveSessionReply = Static<typeof StripeRetrieveSessionReplySchema>;

// ---------------------------------------------------------------------------------------------------------------------

export const StripeUpgradeSubscriptionBodySchema = Type.Object({
  channelId: Type.String(),
  newTierId: Type.String(),
  subscriptionId: Type.String(),
  subscriptionItemId: Type.String(),
  // Return url if payment requires actions like 3DS verification
  returnUrl: Type.String(),
});
export type StripeUpgradeSubscriptionBody = Static<typeof StripeUpgradeSubscriptionBodySchema>;

export const StripeUpgradeSubscriptionReplySchema = StripePaymentIntentDTOSchema;
export type StripeUpgradeSubscriptionReply = Static<typeof StripeUpgradeSubscriptionReplySchema>;

// =====================================================================================================================
// List payment intents
// =====================================================================================================================

export const StripeListPaymentIntentsParamsSchema = Type.Object({ customerId: Type.String() });
export type StripeListPaymentIntentParams = Static<typeof StripeListPaymentIntentsParamsSchema>;

export const StripeListPaymentIntentsQuerystringSchema = Type.Object({
  ending_before: Type.Optional(Type.String()),
  limit: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 })),
  starting_after: Type.Optional(Type.String()),
});
export type StripeListPaymentIntentsQuerystring = Static<typeof StripeListPaymentIntentsQuerystringSchema>;

export const StripeListPaymentIntentsReplySchema = StripeApiListDTOSchema(StripePaymentIntentDTOSchema);
export type StripeListPaymentIntentsReply = Static<typeof StripeListPaymentIntentsReplySchema>;

// =====================================================================================================================
// Set default payment
// =====================================================================================================================

export const StripeSetDefaultPaymentMethodBodySchema = Type.Object({
  paymentMethodId: Type.String(),
  customerId: Type.String(),
});
export type StripeSetDefaultPaymentMethodBody = Static<typeof StripeSetDefaultPaymentMethodBodySchema>;

export const StripeSetDefaultPaymentMethodReplySchema = StripeCustomerDTOSchema;
export type StripeSetDefaultPaymentMethodReply = Static<typeof StripeSetDefaultPaymentMethodReplySchema>;

// =====================================================================================================================
// Update subscription
// =====================================================================================================================

export const StripeUpdateSubscriptionBodySchema = Type.Object({
  cancel: Type.Optional(Type.Boolean()),
  paymentMethodId: Type.Optional(Type.String()),
  subscriptionItemId: Type.Optional(Type.String()),
});
export type StripeUpdateSubscriptionBody = Static<typeof StripeUpdateSubscriptionBodySchema>;

export const StripeUpdateSubscriptionParamsSchema = Type.Object({ subscriptionId: Type.String() });
export type StripeUpdateSubscriptionParams = Static<typeof StripeUpdateSubscriptionParamsSchema>;

export const StripeUpdateSubscriptionReplySchema = StripeSubscriptionDTOSchema;
export type StripeUpdateSubscriptionReply = Static<typeof StripeUpdateSubscriptionReplySchema>;
